/**
 * Confidence utilities for PDF table extraction
 * Handles confidence score calculations and color mapping
 */

/**
 * Get confidence color scheme based on score
 * Only highlights low and medium confidence, high confidence has no color
 * @param {number} confidence - Confidence score between 0 and 1
 * @returns {Object|null} Color scheme with backgroundColor, borderColor, and color, or null for high confidence
 */
export const getConfidenceColor = (confidence) => {
  const score = (confidence || 0) * 100;
  if (score < 50) {
    // Red for low confidence
    return {
      backgroundColor: "rgba(211, 47, 47, 0.25)",
      borderColor: "rgba(183, 28, 28, 0.7)",
      color: "#b71c1c",
    };
  } else if (score < 90) {
    // Yellow for medium confidence
    return {
      backgroundColor: "rgba(255, 193, 7, 0.25)",
      borderColor: "rgba(255, 152, 0, 0.7)",
      color: "#e65100",
    };
  } else {
    // No color for high confidence (≥90%)
    return null;
  }
};

/**
 * Get the lowest confidence score for a cell
 * @param {Object} selectedTable - The table data
 * @param {number} rowIndex - Row index (1-based)
 * @param {number} colIndex - Column index (0-based)
 * @param {number} filterThreshold - Optional confidence threshold for filtering
 * @param {boolean} filterEnabled - Whether filtering is enabled
 * @returns {number|null} Lowest confidence score or null if no data
 */
export const getCellLowestConfidence = (
  selectedTable,
  rowIndex,
  colIndex,
  filterThreshold = 0,
  filterEnabled = false
) => {
  const cellKey = `${rowIndex}_${colIndex}`;
  const cellData = selectedTable[cellKey];

  if (!cellData || !Array.isArray(cellData) || cellData.length === 0) {
    return null;
  }

  const cellInfo = cellData[0];
  if (!cellInfo.words || cellInfo.words.length === 0) {
    return null;
  }

  // Filter words based on threshold if filtering is enabled
  let wordsToConsider = cellInfo.words;
  if (filterEnabled && filterThreshold > 0) {
    wordsToConsider = filterWordsByConfidence(
      cellInfo.words,
      filterThreshold,
      true
    );
  }

  if (wordsToConsider.length === 0) {
    return null;
  }

  // Find the lowest confidence score among considered words
  const confidenceScores = wordsToConsider
    .map((word) => word.confidence)
    .filter((conf) => conf !== undefined && conf !== null);

  if (confidenceScores.length === 0) {
    return null;
  }

  return Math.min(...confidenceScores);
};

/**
 * Get the average confidence score for a cell
 * @param {Object} selectedTable - The table data
 * @param {number} rowIndex - Row index (1-based)
 * @param {number} colIndex - Column index (0-based)
 * @returns {number|null} Average confidence score or null if no data
 */
export const getCellAverageConfidence = (selectedTable, rowIndex, colIndex) => {
  const cellKey = `${rowIndex}_${colIndex}`;
  const cellData = selectedTable[cellKey];

  if (!cellData || !Array.isArray(cellData) || cellData.length === 0) {
    return null;
  }

  const cellInfo = cellData[0];
  if (!cellInfo.words || cellInfo.words.length === 0) {
    return null;
  }

  // Calculate average confidence score among all words in the cell
  const confidenceScores = cellInfo.words
    .map((word) => word.confidence)
    .filter((conf) => conf !== undefined && conf !== null);

  if (confidenceScores.length === 0) {
    return null;
  }

  return (
    confidenceScores.reduce((sum, score) => sum + score, 0) /
    confidenceScores.length
  );
};

/**
 * Format confidence score as percentage string
 * @param {number} confidence - Confidence score between 0 and 1
 * @returns {string} Formatted percentage (e.g., "85.2%")
 */
export const formatConfidencePercentage = (confidence) => {
  if (confidence === null || confidence === undefined) {
    return "N/A";
  }
  return `${((confidence || 0) * 100).toFixed(1)}%`;
};

/**
 * Filter words based on confidence threshold
 * @param {Array} words - Array of word objects with confidence scores
 * @param {number} threshold - Minimum confidence threshold (0-1)
 * @param {boolean} enabled - Whether filtering is enabled
 * @returns {Array} Filtered array of words
 */
export const filterWordsByConfidence = (words, threshold, enabled) => {
  if (!enabled || !words || !Array.isArray(words)) {
    return words || [];
  }

  return words.filter((word) => {
    if (word.confidence === undefined || word.confidence === null) {
      return true;
    }
    return word.confidence >= threshold;
  });
};

/**
 * Sort words by their visual position (top-to-bottom, left-to-right)
 * @param {Array} words - Array of word objects with bbox coordinates
 * @param {number} tolerance - Y-axis tolerance for considering words on same line
 * @returns {Array} Sorted array of words
 */
const sortWordsByPosition = (words, tolerance = 5) => {
  if (!words || !Array.isArray(words)) {
    return [];
  }

  return [...words].sort((a, b) => {
    // If words are on roughly the same line (Y position), sort by X
    if (Math.abs((a.bbox?.y0 || 0) - (b.bbox?.y0 || 0)) < tolerance) {
      return (a.bbox?.x0 || 0) - (b.bbox?.x0 || 0);
    }
    // Otherwise sort by Y position (top to bottom)
    return (a.bbox?.y0 || 0) - (b.bbox?.y0 || 0);
  });
};

/**
 * Get cell text with confidence filtering applied
 * @param {Object} selectedTable - The table data
 * @param {number} rowIndex - Row index (1-based)
 * @param {number} colIndex - Column index (0-based)
 * @param {number} threshold - Minimum confidence threshold (0-1)
 * @param {boolean} enabled - Whether filtering is enabled
 * @returns {string} Filtered cell text
 */
export const getCellTextWithConfidenceFilter = (
  selectedTable,
  rowIndex,
  colIndex,
  threshold,
  enabled
) => {
  const cellKey = `${rowIndex}_${colIndex}`;
  const cellData = selectedTable[cellKey];

  if (!cellData || !Array.isArray(cellData) || cellData.length === 0) {
    return "";
  }

  const cellInfo = cellData[0];
  if (!cellInfo.words || cellInfo.words.length === 0) {
    return "";
  }

  // Sort words by position first to ensure correct reading order
  const sortedWords = sortWordsByPosition(cellInfo.words);
  // Then filter by confidence while maintaining position order
  const filteredWords = filterWordsByConfidence(
    sortedWords,
    threshold,
    enabled
  );
  return filteredWords
    .map((word) => word.text)
    .join(" ")
    .trim();
};
